import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Table,
  Row,
  Col,
  Badge
} from 'reactstrap';
import { translate } from "react-translate";
import classnames from 'classnames';
import '../../assets/css/DoctorDetails.css';

const ITEMS_PER_PAGE = 10;

const DoctorDetailsModal = ({ isOpen, toggle, doctor, t }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(0);
      setSearchTerm('');
      setCurrentPage(1);
    }
  }, [isOpen, doctor]);

  const details = doctor?.associated_details || [];

  const currentPracticeData = useMemo(() => {
    if (!details[activeTab]) return { patients: [], total: 0, paginated: [] };
    
    // Sort patients by last name and first name to group duplicates visually
    let processedPatients = [...(details[activeTab].patients || [])].sort((a, b) => {
        const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
        const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
    });

    // Filter by search term
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      processedPatients = processedPatients.filter(p => 
        (p.firstName && p.firstName.toLowerCase().includes(lowerTerm)) ||
        (p.lastName && p.lastName.toLowerCase().includes(lowerTerm)) ||
        (p.email && p.email.toLowerCase().includes(lowerTerm))
      );
    }

    return {
      allFiltered: processedPatients,
      paginated: processedPatients.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
      ),
      total: processedPatients.length
    };
  }, [details, activeTab, searchTerm, currentPage]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setCurrentPage(1);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(currentPracticeData.total / ITEMS_PER_PAGE);

  const getPracticeDisplayName = (practice, index) => {
      if (practice.name) return practice.name;
      if (practice.practice_name && !practice.practice_name.startsWith("Unknown")) {
          return practice.practice_name;
      }
      return `${t("practice")} ${index + 1}`;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="doctor-details-modal">
      <ModalHeader toggle={toggle}>
        {t("modal-title")}: {doctor?.firstName} {doctor?.lastName}
      </ModalHeader>
      <ModalBody className="doctor-details-content">
        
        <div className="mb-4">
            <h5>
                <Badge color="info" className="mr-2">{doctor?.email}</Badge>
                <small className="text-muted">ID: {doctor?.auth_user_id}</small>
            </h5>
        </div>

        {details.length === 0 ? (
            <div className="text-center p-5 text-muted">
                <i className="fa fa-folder-open fa-3x mb-3"></i>
                <p>{t("no-patients")}</p>
            </div>
        ) : (
            <>
                <Nav tabs className="practice-nav-tabs">
                {details.map((practice, index) => (
                    <NavItem key={index}>
                    <NavLink
                        className={classnames({ active: activeTab === index })}
                        onClick={() => toggleTab(index)}
                    >
                        {getPracticeDisplayName(practice, index)}
                        <Badge color="secondary" pill className="ml-2">
                            {practice.patients ? practice.patients.length : 0}
                        </Badge>
                    </NavLink>
                    </NavItem>
                ))}
                </Nav>

                <TabContent activeTab={activeTab}>
                {details.map((practice, index) => (
                    <TabPane tabId={index} key={index}>
                        <Row>
                            <Col md="12">
                                {/* Practice Details Header */}
                                <div className="bg-light p-3 mb-3 rounded border">
                                    <Row>
                                        <Col md="6">
                                            <p className="mb-1"><strong>{t("practice-name")}:</strong> {practice.name || practice.practice_name}</p>
                                            <p className="mb-1"><strong>{t("address")}:</strong> {practice.street} {practice.postal_code} {practice.city}</p>
                                        </Col>
                                        <Col md="6">
                                            <p className="mb-1"><strong>{t("contact")}:</strong> {practice.contact_person}</p>
                                            <p className="mb-1"><strong>ID:</strong> {practice.practice_id}</p>
                                        </Col>
                                    </Row>
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="text-muted small">
                                        {t("showing")} {currentPracticeData.paginated.length} {t("of")} {currentPracticeData.total} {t("records")}
                                    </span>
                                    <div style={{width: '300px'}}>
                                        <InputGroup>
                                            <InputGroupAddon addonType="prepend">
                                            <InputGroupText>
                                                <i className="fa fa-search" />
                                            </InputGroupText>
                                            </InputGroupAddon>
                                            <Input
                                                placeholder={t("search-patient")}
                                                onChange={handleSearch}
                                                value={searchTerm}
                                            />
                                        </InputGroup>
                                    </div>
                                </div>

                                <div className="patient-list-container">
                                    <Table striped hover responsive size="sm">
                                        <thead className="text-primary">
                                            <tr>
                                                <th>{t("patient-name")}</th>
                                                <th>{t("patient-email")}</th>
                                                <th className="text-right">{t("patient-auth-id")}</th>
                                                <th className="text-center">{t("status")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentPracticeData.paginated.length > 0 ? (
                                                currentPracticeData.paginated.map((patient, pIdx) => (
                                                    <tr key={pIdx} className={patient.is_duplicate ? "text-danger" : ""}>
                                                        <td>
                                                            <i className={`fa ${patient.is_duplicate ? 'fa-files-o' : 'fa-user'} mr-2`}></i>
                                                            {patient.firstName} {patient.lastName}
                                                        </td>
                                                        <td>{patient.email || '-'}</td>
                                                        <td className="text-right small">
                                                            {patient.auth_user_id}
                                                        </td>
                                                        <td className="text-center">
                                                            {patient.is_duplicate && (
                                                                <Badge color="danger" pill>Duplicate</Badge>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-4">
                                                        {t("no-patients")}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {currentPracticeData.total > ITEMS_PER_PAGE && (
                                    <div className="d-flex justify-content-center mt-3 align-items-center">
                                        <Button 
                                            size="sm" 
                                            color="secondary" 
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(prev => prev - 1)}
                                        >
                                            <i className="fa fa-chevron-left" />
                                        </Button>
                                        <span className="mx-3 small">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <Button 
                                            size="sm" 
                                            color="secondary" 
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(prev => prev + 1)}
                                        >
                                            <i className="fa fa-chevron-right" />
                                        </Button>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </TabPane>
                ))}
                </TabContent>
            </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
            {t("btn-close")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default translate("userList")(DoctorDetailsModal);